import type { DonationStatus } from '@impact/types/enums';

/**
 * Pure helpers for the checkout return flow.
 *
 * Contract (embedded /donate/checkout page, StripeCheckoutPage):
 * - success return: `returnTo?donationId=…&eventId=…`
 * - cancel return:  `returnTo?cancelled=1`
 *
 * `session_id`/`campaignId` only appear on stale hosted-checkout (Stripe
 * Checkout Session) URLs — still cleaned, no longer required for success.
 */

export const SETTLEMENT_POLL_INTERVAL_MS = 3_000;
export const SETTLEMENT_TIMEOUT_MS = 60_000;

const RETURN_PARAMS = ['donationId', 'eventId', 'campaignId', 'session_id', 'cancelled'] as const;

export type HostedCheckoutReturn =
  | { kind: 'success'; donationId: string }
  | { kind: 'cancelled' }
  | { kind: 'none' };

export function parseHostedCheckoutReturn(params: URLSearchParams): HostedCheckoutReturn {
  if (params.get('cancelled') === '1') {
    return { kind: 'cancelled' };
  }

  const donationId = params.get('donationId');
  if (donationId) {
    return { kind: 'success', donationId };
  }

  return { kind: 'none' };
}

/** Returns `href` with all hosted-return params removed (for history.replaceState). */
export function buildCleanedReturnUrl(href: string): string {
  const url = new URL(href);
  for (const param of RETURN_PARAMS) {
    url.searchParams.delete(param);
  }
  return url.toString();
}

export type SettlementOutcome = 'polling' | 'paid' | 'failed' | 'cancelled' | 'timeout';

/**
 * Maps a polled donation status + elapsed time to what the return flow should
 * do next. Unknown/missing status (transient query failure) keeps polling
 * until the timeout — the webhook completes registration server-side anyway.
 */
export function resolveSettlementOutcome({
  status,
  elapsedMs,
  timeoutMs = SETTLEMENT_TIMEOUT_MS,
}: {
  status: DonationStatus | undefined;
  elapsedMs: number;
  timeoutMs?: number;
}): SettlementOutcome {
  switch (status) {
    case 'paid':
    case 'refunded':
    case 'partially_refunded':
      return 'paid';
    case 'failed':
      return 'failed';
    case 'cancelled':
      return 'cancelled';
    default:
      return elapsedMs >= timeoutMs ? 'timeout' : 'polling';
  }
}
